import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import {
    BiometricAuth,
    AuthenticateOptions,
    BiometryError,
    BiometryErrorType,
} from '@aparajita/capacitor-biometric-auth';

const BIOMETRIC_IDENTITY_KEY = 'bio_identity';
const BIOMETRIC_SECRET_KEY = 'bio_secret';
const BIOMETRIC_ENABLED_KEY = 'bio_enabled';

@Injectable({
    providedIn: 'root',
})
export class BiometricService {
    /**
     * Check if biometric authentication is available on this device
     */
    async isAvailable(): Promise<boolean> {
        try {
            // Cek ketersediaan plugin secara aman agar tidak memicu error di Zone.js
            if (!Capacitor.isPluginAvailable('BiometricAuthNative')) {
                return false;
            }

            if (typeof BiometricAuth === 'undefined' || !BiometricAuth) {
                return false;
            }
            const info = await BiometricAuth.checkBiometry();
            return info?.isAvailable || false;
        } catch {
            return false;
        }
    }

    /**
     * Check if saved credentials exist for biometric login
     */
    async hasCredentials(): Promise<boolean> {
        const { value: enabled } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
        if (enabled !== 'true') return false;

        const { value: identity } = await Preferences.get({ key: BIOMETRIC_IDENTITY_KEY });
        const { value: secret } = await Preferences.get({ key: BIOMETRIC_SECRET_KEY });
        return !!(identity && secret);
    }

    /**
     * Save credentials for future biometric login
     */
    async saveCredentials(identity: string, secret: string): Promise<void> {
        await Preferences.set({ key: BIOMETRIC_IDENTITY_KEY, value: identity });
        await Preferences.set({ key: BIOMETRIC_SECRET_KEY, value: secret });
        await Preferences.set({ key: BIOMETRIC_ENABLED_KEY, value: 'true' });
    }

    /**
     * Retrieve saved credentials
     */
    async getCredentials(): Promise<{ identity: string; secret: string } | null> {
        const { value: identity } = await Preferences.get({ key: BIOMETRIC_IDENTITY_KEY });
        const { value: secret } = await Preferences.get({ key: BIOMETRIC_SECRET_KEY });
        if (identity && secret) {
            return { identity, secret };
        }
        return null;
    }

    /**
     * Clear stored biometric credentials (e.g. on logout)
     */
    async clearCredentials(): Promise<void> {
        await Preferences.remove({ key: BIOMETRIC_IDENTITY_KEY });
        await Preferences.remove({ key: BIOMETRIC_SECRET_KEY });
        await Preferences.remove({ key: BIOMETRIC_ENABLED_KEY });
    }

    /**
     * Prompt the biometric sensor and return true if authenticated
     */
    async authenticate(): Promise<{ success: boolean; errorMessage?: string }> {
        // Cek ketersediaan plugin secara aman
        if (!Capacitor.isPluginAvailable('BiometricAuthNative') || typeof BiometricAuth === 'undefined' || !BiometricAuth) {
            return { success: false, errorMessage: 'Fitur sidik jari tidak didukung di perangkat ini.' };
        }

        const options: AuthenticateOptions = {
            reason: 'Login ke Kossaya dengan sidik jari',
            cancelTitle: 'Batal',
            allowDeviceCredential: true,
            iosFallbackTitle: 'Gunakan PIN',
            androidTitle: 'Autentikasi Biometrik',
            androidSubtitle: 'Gunakan sidik jari untuk masuk',
        };

        try {
            await BiometricAuth.authenticate(options);
            return { success: true };
        } catch (error: any) {
            const biometryError = error as BiometryError;
            const cancelled =
                biometryError.code === BiometryErrorType.userCancel ||
                biometryError.code === BiometryErrorType.systemCancel ||
                biometryError.code === BiometryErrorType.appCancel;

            if (cancelled) {
                return { success: false, errorMessage: '' };
            }

            return {
                success: false,
                errorMessage: this.getFriendlyError(biometryError.code),
            };
        }
    }

    private getFriendlyError(code: BiometryErrorType): string {
        switch (code) {
            case BiometryErrorType.authenticationFailed:
                return 'Sidik jari tidak dikenali. Coba lagi.';
            case BiometryErrorType.biometryLockout:
                return 'Terlalu banyak percobaan. Silakan login dengan password.';
            case BiometryErrorType.biometryNotAvailable:
                return 'Sensor sidik jari tidak tersedia.';
            case BiometryErrorType.biometryNotEnrolled:
                return 'Tidak ada sidik jari terdaftar di perangkat ini.';
            default:
                return 'Autentikasi sidik jari gagal. Silakan login manual.';
        }
    }
}
